import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { CLASSIFICATION } from 'shared/constants/organization.constants';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { SelectGroup } from '@radix-ui/react-select';
import { X } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { createOrganization } from '@/api/organization.api.private';
import { Checkbox } from '@/components/ui/checkbox';
import { useNavigate } from 'react-router-dom';

const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

const ORGANIZATION_NEW_SCHEMA = z.object({
  name: z.string().min(2, {
    message: 'Name must be at least 2 characters.',
  }),
  logo: z.string(),
  domain: z.array(
    z.string().regex(domainRegex, {
      message: 'Invalid domain format',
    }),
  ),
  aboutUrl: z.string().url({ message: 'Invalid URL' }),
  classification: z.enum(Object.values(CLASSIFICATION)),
  cloneFromOrganization: z
    .object({
      organizationId: z.string().nullable().optional(),
      includeContent: z.boolean().optional(),
    })
    .optional(),
  defaultOwner: z.object({
    email: z
      .string({
        required_error: 'Email is required',
      })
      .email(),
    firstName: z.string().min(2, {
      message: 'First name is required',
    }),
    lastName: z.string().min(2, {
      message: 'Last name is required',
    }),
  }),
});

export default function OrganizationForm({ organizationList = [], callback = () => {} }) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(ORGANIZATION_NEW_SCHEMA),
    defaultValues: {
      name: '',
      logo: 'c8-health.png',
      domain: [],
      aboutUrl: 'https://c8health.com',
      classification: CLASSIFICATION.INTERNAL,
      cloneFromOrganization: {
        organizationId: null,
        includeContent: false,
      },
      defaultOwner: {
        email: '',
        firstName: '',
        lastName: '',
      },
    },
  });

  const addDomain = e => {
    if (newDomain && e.key === 'Enter') {
      e.preventDefault();
      try {
        // check if domain is valid
        z.string()
          // using regex from zod4 regexes.domain: https://zod.dev/api#string-formats
          .regex(/^([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/)
          .parse(newDomain);

        const domains = form.getValues('domain');

        if (domains.includes(newDomain)) {
          form.setError('domain', {
            message: 'Domain already exists',
          });
        } else {
          form.setValue('domain', [...domains, newDomain]);
          setNewDomain('');
        }
      } catch (e) {
        form.setError('domain', {
          message: 'Domain should be like example.com',
        });
      }
    }
  };

  const handleDomainChange = e => {
    form.clearErrors('domain');
    setNewDomain(e.target.value);
  };

  const onSubmit = async formData => {
    setLoading(true);

    // Remove cloneFromOrganization if organizationId is null
    if (!formData.cloneFromOrganization.organizationId) {
      delete formData.cloneFromOrganization;
    }

    const { data, error } = await createOrganization({
      data: formData,
    });

    setLoading(false);

    if (error) {
      toast({ status: 'error', title: data.message, variant: 'destructive' });
      return;
    }

    toast({
      status: 'success',
      title: `Organization successfully created`,
    });

    form.reset();
    setOpen(false);
    callback();

    navigate(`/dashboard/organizations/${data.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="ml-6 mb-3">New Organization</Button>
      </DialogTrigger>
      <DialogContent className="min-w-fit overflow-y-scroll h-4/5">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>New organization</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Form {...form}>
            <form
              id="organization"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 w-full">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Name</FormLabel>
                    <FormControl>
                      <Input placeholder="C8 Health" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="classification"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Classification</FormLabel>
                    <FormControl>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectGroup>
                            {Object.values(CLASSIFICATION).map(value => (
                              <SelectItem key={value} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Separator />
              <div className="flex flex-row gap-10">
                <div className="flex flex-col gap-5 flex-1">
                  <FormField
                    control={form.control}
                    name="domain"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-bold">Allowed Domains</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="c8health.com"
                            value={newDomain}
                            onChange={handleDomainChange}
                            onKeyDown={addDomain}
                          />
                        </FormControl>
                        <FormMessage />

                        <div className="flex-grow-0">
                          {field.value.length > 0 ? (
                            field.value.map((domain, index) => (
                              <Badge key={index} className="p-2 mr-2 my-2 hover:bg-primary/100">
                                {domain}
                                <X
                                  className="cursor-pointer"
                                  size={16}
                                  onClick={e => {
                                    e.preventDefault();
                                    field.onChange(field.value.filter(d => d !== domain));
                                  }}
                                />
                              </Badge>
                            ))
                          ) : (
                            <FormDescription className="h-10">No domain added yet</FormDescription>
                          )}
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <Separator />
              <FormField
                control={form.control}
                name="defaultOwner.email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Default Owner Email</FormLabel>
                    <FormControl>
                      <Input placeholder="john-doe@mail.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="defaultOwner.firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Default Owner First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="defaultOwner.lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Default Owner Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Separator />
              <FormField
                control={form.control}
                name="cloneFromOrganization.organizationId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-bold">Clone from Organization</FormLabel>
                    <FormControl>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectGroup>
                            {organizationList.map(({ id, name }) => (
                              <SelectItem key={id} value={id}>
                                {name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cloneFromOrganization.includeContent"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Checkbox
                        className="text-center"
                        {...field}
                        onCheckedChange={checked =>
                          form.setValue('cloneFromOrganization.includeContent', checked)
                        }
                      />
                    </FormControl>
                    <FormLabel className="ml-3 text-center">Include Content</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="submit" form="organization" loading={loading} disabled={loading}>
                  Submit
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
